/**
 * The maps the therapist chose to show this patient.
 *
 * A list only: the map itself opens on its own page, full width, because a
 * genogram is read by moving around it. Nothing here can change a map — the
 * portal has no write path to the board at all, which is what makes it safe
 * to show a document the therapist keeps as a clinical record.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Network, Share2 } from "lucide-react";

import { listPortalSystemicMaps } from "../../application/portalServices";
import type { PortalMapSummary } from "../../../systemic_map";

export default function PortalSystemicMapsPage() {
    const { t, i18n } = useTranslation();
    const [maps, setMaps] = useState<PortalMapSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;

        listPortalSystemicMaps()
            .then((data) => {
                if (cancelled) return;
                setMaps(data);
                setLoading(false);
            })
            .catch(() => {
                if (cancelled) return;
                setError(true);
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    const shownOn = (iso: string) =>
        new Date(iso).toLocaleDateString(i18n.language, {
            day: "numeric",
            month: "long",
            year: "numeric",
        });

    return (
        <>
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-content-primary">
                    {t("portal.systemicMaps.title")}
                </h1>
                <p className="mt-1 text-sm text-content-tertiary">
                    {t("portal.systemicMaps.subtitle")}
                </p>
            </div>

            {loading && <p className="text-sm text-gray-500">{t("common.loading")}</p>}

            {!loading && error && (
                <p role="alert" className="text-sm text-red-600">
                    {t("portal.systemicMaps.loadError")}
                </p>
            )}

            {!loading && !error && maps.length === 0 && (
                <div className="glass-effect glass-border rounded-2xl border p-8 text-center">
                    <Share2
                        size={24}
                        aria-hidden="true"
                        className="mx-auto mb-3 text-content-tertiary"
                    />
                    <p className="text-sm text-content-secondary">
                        {t("portal.systemicMaps.empty")}
                    </p>
                </div>
            )}

            {!loading && !error && maps.length > 0 && (
                <ul className="grid gap-4 sm:grid-cols-2">
                    {maps.map((map) => (
                        <li key={map.id}>
                            <Link
                                to={`/portal/systemic-maps/${map.id}`}
                                className="glass-effect glass-border flex h-full flex-col gap-2 rounded-2xl border p-5 shadow-sm transition-colors duration-fast hover:bg-surface-sunken focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                            >
                                <span className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-content-tertiary">
                                    <Network size={14} aria-hidden="true" />
                                    {t(`systemicMap.kind.${map.kind}`)}
                                </span>
                                <span className="text-base font-semibold text-content-primary">
                                    {map.title}
                                </span>
                                <span className="text-xs text-content-tertiary">
                                    {t("portal.systemicMaps.sharedOn", {
                                        date: shownOn(map.shared_at),
                                    })}
                                </span>
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </>
    );
}
